import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/containers/contexts/auth-provider';
import { useAvailableSites } from './api/use-available-sites';
import { useAvailableTenants } from './api/use-available-tenants';

type ReturnType = {
  tenantContext: boolean;
  siteContext: boolean;
  hasFetchedAccess: boolean;
};

export const useHasViewContextAccess = (): ReturnType => {
  const auth = useAuth();

  const [access, setAccess] = useState({
    tenantContext: false,
    siteContext: false,
  });

  const [hasFetchedAccess, setHasFetchedAccess] = useState(false);

  const availableTenants = useAvailableTenants(!!auth.session?.access_token);

  // Check if user has access to tenant context
  useEffect(() => {
    if (
      availableTenants.isSuccess &&
      availableTenants.data?.resources.length > 0
    ) {
      setAccess(prev => ({
        ...prev,
        tenantContext: true,
      }));
    }

    if (
      availableTenants.isError ||
      (!availableTenants.isPending &&
        availableTenants.data?.resources.length === 0)
    ) {
      setAccess(prev => ({
        ...prev,
        tenantContext: false,
      }));
    }
  }, [
    availableTenants.isSuccess,
    availableTenants.isError,
    availableTenants.data?.resources.length,
    availableTenants.isPending,
  ]);

  const availableSites = useAvailableSites(!!auth.session?.access_token);

  // Check if user has access to site context
  useEffect(() => {
    if (availableSites.isSuccess && availableSites.data?.resources.length > 0) {
      setAccess(prev => ({
        ...prev,
        siteContext: true,
      }));
    }

    if (
      availableSites.isError ||
      (!availableSites.isPending && availableSites.data?.resources.length === 0)
    ) {
      setAccess(prev => ({
        ...prev,
        siteContext: false,
      }));
    }
  }, [
    availableSites.isSuccess,
    availableSites.isError,
    availableSites.data?.resources.length,
    availableSites.isPending,
  ]);

  useEffect(() => {
    if (
      (availableTenants.isSuccess || availableTenants.isError) &&
      (availableSites.isSuccess || availableSites.isError)
    ) {
      setHasFetchedAccess(true);
    }
  }, [
    availableSites.isError,
    availableSites.isSuccess,
    availableTenants.isError,
    availableTenants.isSuccess,
  ]);

  const accessObject = useMemo(
    () => ({
      tenantContext: access.tenantContext,
      siteContext: access.siteContext,
      hasFetchedAccess,
    }),
    [access.tenantContext, access.siteContext, hasFetchedAccess],
  );

  return accessObject;
};
